import type React from "react";
import { useMemo } from "react";
import { Check, X, Shield } from "lucide-react";

import { formatPermission } from "../../../../utils/utils";
import { useRoleContext } from "../../../../context/RolesContext";

const PermissionComparison: React.FC = () => {
  const { activeRolePermissions, sysData, isLoading } = useRoleContext();

  const calculatedMissingPermissions = useMemo(() => {
    if (!sysData?.data?.rolePermissions || !activeRolePermissions) {
      return [];
    }

    return sysData.data.rolePermissions.filter(
      (permission) => !activeRolePermissions.includes(permission)
    );
  }, [activeRolePermissions, sysData?.data?.rolePermissions]);

  const calculatedHasFullAccess = useMemo(() => {
    if (!sysData?.data?.rolePermissions || !activeRolePermissions) {
      return false;
  }

    return (
      calculatedMissingPermissions.length === 0 &&
      activeRolePermissions.length === sysData.data.rolePermissions.length
    );
  }, [
    activeRolePermissions,
    calculatedMissingPermissions,
    sysData?.data?.rolePermissions,
  ]);

  if (!activeRolePermissions.length && !sysData?.data?.rolePermissions) {
    return <div className="p-4 text-gray-500">Loading permissions...</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* What you can access */}
      <div className="bg-white rounded-lg overflow-hidden shadow-sm border">
        <div className="bg-green-50 p-4 border-b">
          <h2 className="text-base font-medium flex items-center text-green-700">
            <Check size={18} className="mr-2" />
            What you can access
          </h2>
        </div>
        <div className="h-[400px] overflow-auto">
          {activeRolePermissions.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No permissions granted
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {activeRolePermissions.map((permission, index) => (
                <div
                  key={index}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start">
                    <Shield size={16} className="mr-3 mt-0.5 text-green-500" />
                    <div>
                      <span className="font-medium text-sm text-gray-700">
                        Can{" "}
                      </span>
                      <span className="text-sm text-gray-600">
                        {formatPermission(permission)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* What you can't access */}
      <div className="bg-white rounded-lg overflow-hidden shadow-sm border">
        <div className="bg-red-50 p-4 border-b">
          <h2 className="text-base font-medium flex items-center text-red-700">
            <X size={18} className="mr-2" />
            What you can't access
          </h2>
        </div>
        <div className="h-[400px] overflow-auto">
          {calculatedHasFullAccess ? (
            <div className="flex flex-col items-center justify-center p-10 text-center h-full">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Check size={24} className="text-green-600" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-gray-800">
                Full Access
              </h3>
              <p className="text-sm text-gray-600 max-w-xs">
                This role has access to all available permissions in the system.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {calculatedMissingPermissions.map((permission, index) => (
                <div
                  key={index}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start">
                    <X size={16} className="mr-3 mt-0.5 text-red-500" />
                    <div>
                      <span className="font-medium text-sm text-gray-700">
                        Can't{" "}
                      </span>
                      <span className="text-sm text-gray-600">
                        {formatPermission(permission)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionComparison;
